import useSWRInfinite from 'swr/infinite';
import styled from 'styled-components';

import Button from 'components/Button';
import LoadingSpinner from 'components/LoadingSpinner';
import SessionCard from 'components/Games/SessionCard';

import { fetcher } from 'hooks/useFetch';

import { useAccount } from 'context/AccountProvider';
import { useClaysLocation } from 'context/ClaysLocationProvider';

import BREAKPOINTS from 'styles/breakpoints';
import { GridOuter, GridInner, columns } from 'styles/grid';
import { H2, fluidType } from 'styles/typography';

import PageLayout from './PageLayout';

const Title = styled(H2)`
  margin-bottom: 20px;
  text-align: center;

  ${BREAKPOINTS.medium`
    margin-top: ${fluidType(40, 70)};
  `}
`;

const Grid = styled(GridInner)`
  ${BREAKPOINTS.small`
    max-width: 714px;
  `}

  ${BREAKPOINTS.medium`
    max-width: 860px;
  `}

  ${BREAKPOINTS.large`
    max-width: 1340px;
  `}
`;

const Session = styled(SessionCard)`
  ${columns(null, 12)}
  justify-self: center;

  ${BREAKPOINTS.small`
    ${columns(null, 6)}
  `}

  ${BREAKPOINTS.large`
    ${columns(null, 4)}
  `}
`;

const ButtonContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const ErrorMessage = styled.p`
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
`;

const P = styled.p`
  margin: 0 0 40px;
`;

const Description = styled.p`
  text-align: center;
  opacity: 1;
  margin-bottom: 50px;

  ${BREAKPOINTS.medium`
    margin-bottom: 100px;
  `}
`;

const GamesSessions = () => {
  const { email } = useAccount();
  const { sevenroomsUrl } = useClaysLocation();

  const recordSize = 3;
  const { data, error, size, setSize } = useSWRInfinite(
    (index) => {
      const recordId = index * recordSize;
      return `/api/game/sessions/${email}?recordId=${recordId}&recordSize=${recordSize}`;
    },
    fetcher,
    { revalidateIfStale: false, revalidateOnFocus: false }
  );

  const loadMore = () => {
    setSize(size + 1);
  };

  const isSessionData = data?.[0]?.sessionsData?.length;
  const isLoading =
    (!data && !error) ||
    (size > 0 && data && typeof data[size - 1] === 'undefined');
  const hasMore = data?.[data?.length - 1].hasMore;

  return (
    <PageLayout>
      <Title as="h1">My Sessions</Title>
      <Description>Click on a session for more details</Description>

      {isLoading && !isSessionData ? (
        <LoadingSpinner />
      ) : (
        <>
          {isSessionData ? (
            <>
              <GridOuter>
                <Grid>
                  {data?.map(({ sessionsData }, pageIndex) =>
                    sessionsData.map((session, index) => (
                      <Session
                        key={session.sessionId}
                        index={index + 1 + pageIndex * recordSize}
                        {...session}
                      />
                    ))
                  )}
                </Grid>
              </GridOuter>

              {isLoading && <LoadingSpinner />}

              {hasMore && !isLoading && (
                <ButtonContainer>
                  <Button onClick={loadMore}>Load more</Button>
                </ButtonContainer>
              )}
            </>
          ) : (
            <>
              {error ? (
                <ErrorMessage>
                  Sorry, there was a problem fetching your game data
                </ErrorMessage>
              ) : (
                <EmptyState>
                  <P>It looks like you haven&apos;t played a session yet</P>
                  <Button href={sevenroomsUrl}>Book a new game</Button>
                </EmptyState>
              )}
            </>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default GamesSessions;
